import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface LanguageContextType {
  language: 'ko' | 'en';
  setLanguage: (lang: 'ko' | 'en') => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

const translations = {
  ko: {
    // Navigation
    'nav.home': '홈',
    'nav.contact': '문의하기',
    'nav.comingsoon': '준비 중',
    
    // Footer
    'footer.description': '블록체인 기반 의료 데이터 거래 플랫폼으로 개인, 연구자, 정부가 안전하고 투명하게 의료 데이터를 거래할 수 있습니다.',
    'footer.quicklinks': '빠른 링크',
    'footer.support': '지원',
    'footer.helpcenter': '도움말 센터',
    'footer.documentation': '문서',
    'footer.contactus': '문의하기',
    'footer.privacy': '개인정보처리방침',
    'footer.terms': '이용약관',
    'footer.copyright': '© 2025 Medivine. 모든 권리 보유.',
    
    // Hero Section
    'hero.title': 'Medivine',
    'hero.subtitle': '의료 데이터의 소유권을 되찾으세요. 안전하고 투명한 의료 데이터 거래 플랫폼에서 당신의 데이터로 수익을 창출하고, 의학 발전에 기여하세요.',
    'hero.getstarted': '시작하기',
    'hero.learnmore': '자세히 알아보기',
    
    // Contact Us
    'contact.title': '문의하기',
    'contact.subtitle': 'Medivine에 대한 문의사항이 있으시면 언제든지 연락해주세요. 빠른 시일 내에 답변드리겠습니다.',
    'contact.contactInfo': '연락처 정보',
    'contact.email': '이메일',
    'contact.responsetime': '응답 시간',
    'contact.region': '지역',
    'contact.faq': '자주 묻는 질문',
    'contact.form.name': '이름',
    'contact.form.email': '이메일',
    'contact.form.subject': '제목',
    'contact.form.message': '메시지',
    'contact.form.submit': '문의하기',
    'contact.form.submitting': '전송 중...',
    'contact.form.emailClientOpened': '이메일 클라이언트가 열렸습니다. 문의사항을 작성해주세요.',
    'contact.form.errorSending': '문의 전송 중 오류가 발생했습니다.',
    'contact.responsetimeDetail': '영업일 기준 24시간 이내',
    'contact.regionDetail': '대한민국',
    'contact.faq1.question': 'Medivine은 어떤 서비스인가요?',
    'contact.faq1.answer': 'Medivine은 블록체인 기반 의료 데이터 거래 플랫폼으로, 개인, 연구자, 정부가 안전하고 투명하게 의료 데이터를 거래할 수 있습니다.',
    'contact.faq2.question': '데이터 보안은 어떻게 보장되나요?',
    'contact.faq2.answer': '블록체인 기술과 암호화를 통해 데이터의 무결성과 개인정보 보호를 보장합니다.',
    'contact.form.title': '문의하기',
    'contact.form.namePlaceholder': '이름을 입력해주세요',
    'contact.form.emailPlaceholder': '이메일을 입력해주세요',
    'contact.form.subjectPlaceholder': '문의 제목을 입력해주세요',
    'contact.form.messagePlaceholder': '문의 내용을 자세히 입력해주세요',
    
    // Coming Soon
    'comingsoon.title': '준비 중입니다',
    'comingsoon.subtitle': '더 나은 서비스를 위해 열심히 준비하고 있습니다.',
    'comingsoon.description': '곧 멋진 기능들과 함께 찾아뵙겠습니다. 조금만 기다려주세요!',
    'comingsoon.gohome': '홈으로 돌아가기',
    'comingsoon.contact': '문의하기',
    'comingsoon.progress': '개발 진행률',
    'comingsoon.feature1.title': '향상된 보안',
    'comingsoon.feature1.description': '최신 암호화 기술로 데이터를 보호합니다.',
    'comingsoon.feature2.title': '빠른 처리',
    'comingsoon.feature2.description': '더욱 빠르고 효율적인 거래 처리를 제공합니다.',
    'comingsoon.feature3.title': '사용자 경험',
    'comingsoon.feature3.description': '직관적이고 편리한 인터페이스를 제공합니다.',
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.contact': 'Contact',
    'nav.comingsoon': 'Coming Soon',
    
    // Footer
    'footer.description': 'A blockchain-based medical data trading platform where individuals, researchers, and governments can safely and transparently trade medical data.',
    'footer.quicklinks': 'Quick Links',
    'footer.support': 'Support',
    'footer.helpcenter': 'Help Center',
    'footer.documentation': 'Documentation',
    'footer.contactus': 'Contact Us',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.copyright': '© 2025 Medivine. All rights reserved.',
    
    // Hero Section
    'hero.title': 'Medivine',
    'hero.subtitle': 'Reclaim ownership of your medical data. Generate revenue from your data and contribute to medical advancement through our safe and transparent medical data trading platform.',
    'hero.getstarted': 'Get Started',
    'hero.learnmore': 'Learn More',
    
    // Contact Us
    'contact.title': 'Contact Us',
    'contact.subtitle': 'If you have any questions about Medivine, please feel free to contact us. We will respond as soon as possible.',
    'contact.contactInfo': 'Contact Information',
    'contact.email': 'Email',
    'contact.responsetime': 'Response Time',
    'contact.region': 'Region',
    'contact.faq': 'Frequently Asked Questions',
    'contact.form.name': 'Name',
    'contact.form.email': 'Email',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message',
    'contact.form.submit': 'Send Inquiry',
    'contact.form.submitting': 'Sending...',
    'contact.form.emailClientOpened': 'Email client has been opened. Please write your inquiry.',
    'contact.form.errorSending': 'An error occurred while sending your inquiry.',
    'contact.responsetimeDetail': 'Within 24 hours on business days',
    'contact.regionDetail': 'South Korea',
    'contact.faq1.question': 'What is Medivine?',
    'contact.faq1.answer': 'Medivine is a blockchain-based medical data trading platform where individuals, researchers, and governments can safely and transparently trade medical data.',
    'contact.faq2.question': 'How is data security guaranteed?',
    'contact.faq2.answer': 'Data integrity and privacy protection are ensured through blockchain technology and encryption.',
    'contact.form.title': 'Contact Us',
    'contact.form.namePlaceholder': 'Please enter your name',
    'contact.form.emailPlaceholder': 'Please enter your email',
    'contact.form.subjectPlaceholder': 'Please enter the inquiry subject',
    'contact.form.messagePlaceholder': 'Please enter your inquiry details',
    
    // Coming Soon
    'comingsoon.title': 'Coming Soon',
    'comingsoon.subtitle': 'We are working hard to prepare better services.',
    'comingsoon.description': 'We will be back soon with amazing features. Please wait a little longer!',
    'comingsoon.gohome': 'Go Home',
    'comingsoon.contact': 'Contact Us',
    'comingsoon.progress': 'Development Progress',
    'comingsoon.feature1.title': 'Enhanced Security',
    'comingsoon.feature1.description': 'Protect data with the latest encryption technology.',
    'comingsoon.feature2.title': 'Fast Processing',
    'comingsoon.feature2.description': 'Provide faster and more efficient transaction processing.',
    'comingsoon.feature3.title': 'User Experience',
    'comingsoon.feature3.description': 'Provide an intuitive and convenient interface.',
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<'ko' | 'en'>('ko');

  useEffect(() => {
    // Load saved language preference
    const savedLanguage = localStorage.getItem('medivine-language') as 'ko' | 'en';
    if (savedLanguage && (savedLanguage === 'ko' || savedLanguage === 'en')) {
      setLanguage(savedLanguage);
    }
  }, []);

  const handleSetLanguage = (lang: 'ko' | 'en') => {
    setLanguage(lang);
    localStorage.setItem('medivine-language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key as keyof typeof translations.ko] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
